<template>
  <div
      class="editorPanel textPanel tw-left-2 tw-right-2 lg:tw-left-auto tw-bg-white tw-absolute tw-bottom-0 tw-transition tw-ease-out tw-duration-500 tw-rounded-t-[24px] lg:tw-rounded-tr-none lg:tw-rounded-l-[24px] lg:tw-w-[400px] lg:tw-right-0 lg:tw-bottom-auto lg:tw-top-1/2 lg:tw-translate-y-[-50%] tw-z-[2] tw-overflow-hidden"
      :class="{ panelHidden: !showTextPanel }"
      :style="panelStyle"
  >
    <div ref="panelContentRef" class="tw-flex tw-flex-col tw-overflow-y-auto">
    <div class="textOptions tw-flex tw-justify-between tw-p-4 tw-px-8">
      <img
          :src="getAssetPath('/assets/images/collapseIcon.svg')"
          alt="Close Button"
          @click="closePanel"
          class="tw-cursor-pointer"
      />
      <img
          :class="{ activeTab: editMode }"
          :src="getAssetPath('/assets/images/keyboard.svg')"
          alt="Typing Button"
          @click="startEditing()"
          class="tw-cursor-pointer"
      />
      <img
          :class="{ activeTab: activeTab === 'fontPicker' }"
          :src="getAssetPath('/assets/images/fontIcon.svg')"
          alt="Font Button"
          @click="activeTab = 'fontPicker'"
          class="tw-cursor-pointer"
      />
      <img
          :class="{ activeTab: activeTab === 'textStylePanel' }"
          :src="getAssetPath('/assets/images/alignIcon.svg')"
          alt="Align Button"
          @click="activeTab = 'textStylePanel'"
          class="tw-cursor-pointer"
      />
      <img
          v-if="!areColorsDisabled"
          :class="{ activeTab: activeTab === 'colorPicker' }"
          :src="getAssetPath('/assets/images/colorIcon.png')"
          alt="Color Button"
          @click="activeTab = 'colorPicker'"
          class="tw-cursor-pointer colorPickerIcon"
      />
    </div>

    <FontPanel
        v-if="activeTab === 'fontPicker'"
        :selectedElement="selectedElement"
        @onFontSelected="setFont"
        :companyFonts="companyFonts"
    />

    <div
        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-8"
        v-if="activeTab === 'colorPicker'"
    >
      <ColorPicker
          v-model="color"
          @update:model-value="setTextColor"
          :disableAlpha="true"
          :disableFields="true"
      />
      <div
          class="tw-flex tw-justify-start tw-items-center tw-w-full tw-rounded-l-full tw-rounded-r-full tw-bg-bgDark tw-border-borderLight20 tw-border-1 tw-border-solid tw-mb-4"
      >
        <div
            :style="{ backgroundColor: color }"
            class="tw-border-none tw-text-black tw-rounded-l-full tw-rounded-r-full tw-font-medium tw-text-xl tw-p-2 tw-w-8 tw-h-8"
        ></div>
        <div class="tw-ml-[-2rem] tw-flex-1 tw-flex tw-justify-center tw-font-sans">
          <input
              class="tw-bg-transparent tw-border-none tw-text-xl tw-text-center tw-outline-none tw-text-white"
              style="padding: 0; width: auto"
              type="text"
              v-model="color"
              @input="setTextColor"
          />
        </div>
      </div>
    </div>

    <TextStylePanel
        v-if="activeTab === 'textStylePanel'"
        :selectedElement="selectedElement"
        @onAlignChange="changeTextAlign"
        @onFontSizeChange="changeFontSize"
        @onFontDecoration="changeFontDecoration"
    />
    </div>
  </div>
</template>

<script>
import FontPanel from "./FontPanel.vue";
import FontFaceObserver from "@/utils/fontObserver";
import { Chrome } from "@ckpack/vue-color";
import TextStylePanel from "./TextStylePanel.vue";
import debounce from "@/utils/helpers/debounce";
import { colors } from "@/constants/colors";
import { expandHexTo6 } from "@/utils/helpers/switchPixelsColor";
import systemFonts from "@/constants/systemFonts";
import { getAssetPath } from "@/utils/assetPath";

export default {
  setup() {
    return {
      getAssetPath
    };
  },
  components: {
    FontPanel,
    ColorPicker: Chrome,
    TextStylePanel,
  },
  props: {
    showTextPanel: {
      required: true,
      type: Boolean,
    },
    showColorPicker: {
      required: true,
      type: Boolean,
    },
    selectedElement: {
      type: Object,
    },
    areColorsDisabled: {
      type: Boolean,
    },
    companyFonts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTab: "textStylePanel",
      color: expandHexTo6(colors.black),
      editMode: false,
      panelHeight: 0,
      resizeObserver: null,
    };
  },
  computed: {
    panelStyle() {
      const maxH = (window.innerHeight * 64) / 100;
      const transformTransition = "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      const heightTransition = this.panelHeight ? ", height 0.4s ease-out" : "";
      return {
        height: this.panelHeight ? `${this.panelHeight}px` : "auto",
        maxHeight: `${maxH}px`,
        transition: transformTransition + heightTransition,
      };
    },
  },
  watch: {
    showTextPanel(val) {
      if (val) {
        this.$nextTick(() => {
          this.updatePanelHeight();
          if (this.$refs.panelContentRef && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
            this.resizeObserver.observe(this.$refs.panelContentRef);
          }
        });
      }
    },
    activeTab() {
      this.$nextTick(() => this.updatePanelHeight());
    },
    selectedElement: {
      deep: true,
      handler: function () {
        if (!this.selectedElement) return;

        this.editMode = this.selectedElement.isEditing;
        if (this.editMode) {
          if (
              this.editMode &&
              this.selectedElement.styles &&
              this.selectedElement.styles[0] &&
              this.selectedElement.styles[0][0]
          ) {
            this.color = expandHexTo6(this.selectedElement.styles[0][0].fill);
          } else this.setTextColor();
        }
      },
    },
  },
  mounted() {
    this.fireDebouncedModify = debounce(() => {
      window.activeCanvas.fire("object:modified");
    }, 500);

    if (this.showTextPanel) {
      this.$nextTick(() => {
        this.updatePanelHeight();
        if (this.$refs.panelContentRef && !this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
          this.resizeObserver.observe(this.$refs.panelContentRef);
        }
      });
    }
  },
  beforeUnmount() {
    if (this.resizeObserver && this.$refs.panelContentRef) {
      this.resizeObserver.unobserve(this.$refs.panelContentRef);
    }
  },
  methods: {
    updatePanelHeight() {
      if (!this.$refs.panelContentRef || !this.showTextPanel) return;
      const el = this.$refs.panelContentRef;
      const maxPx = (window.innerHeight * 64) / 100;
      this.panelHeight = Math.min(el.scrollHeight, maxPx);
    },
    closePanel() {
      this.$emit("showTextPanel", false);
      this.activeTab = "textStylePanel";
      window.activeCanvas.discardActiveObject();
      window.activeCanvas.renderAll();
    },

    toggleStyle(value = true, styleType) {
      const { selectionStart, selectionEnd, text } = this.selectedElement;
      let posStart = selectionStart;
      let posEnd = selectionEnd;

      const selectionExist = posEnd > posStart;
      if (!selectionExist && text) {
        posStart = 0;
        posEnd = text.length;
      }

      if (text) {
        const selectionStyles = this.selectedElement.getSelectionStyles(
            posStart,
            posEnd
        );
        const isAllSameStyle = selectionStyles.every(
            (style) => style[styleType] === value
        );

        if (isAllSameStyle && styleType !== "fill") {
          selectionStyles.forEach((style) => {
            delete style[styleType];
          });
        } else {
          this.selectedElement.setSelectionStyles(
              {
                [styleType]: value,
              },
              posStart,
              posEnd
          );
        }
      } else {
        this.selectedElement.set({
          fill: this.color,
        });
      }
    },

    removeStyle(styleType) {
      const { selectionStart, selectionEnd, text } = this.selectedElement;
      let posStart = selectionStart;
      let posEnd = selectionEnd;

      const selectionExist = posEnd > posStart;
      if (!selectionExist && text) {
        posStart = 0;
        posEnd = text.length;
      }

      if (text) {
        const selectionStyles = this.selectedElement.getSelectionStyles(
            posStart,
            posEnd
        );
        selectionStyles.forEach((style) => {
          delete style[styleType];
        });
      }
    },

    changeFontDecoration(type) {
      switch (type) {
        case "bold":
          this.toggleStyle(type, "fontWeight");
          break;
        case "italic":
          this.toggleStyle(type, "fontStyle");
          break;
        case "underline":
          this.removeStyle("linethrough");
          this.toggleStyle(true, "underline");
          break;
        case "linethrough":
          this.removeStyle("underline");
          this.toggleStyle(true, "linethrough");
          break;
        default:
          this.toggleStyle(true, type);
          break;
      }

      window.activeCanvas.renderAll();
      window.activeCanvas.fire("object:modified");
    },

    changeFontSize(fontSize) {
      this.selectedElement.set("fontSize", fontSize);
      window.activeCanvas.renderAll();
      this.$emit("onFontSizeChange", fontSize);
      this.fireDebouncedModify();
    },

    changeTextAlign(align) {
      this.selectedElement.set("textAlign", align);
      window.activeCanvas.renderAll();
      window.activeCanvas.fire("object:modified");
    },

    preventHashtagDeletion(event) {
      // Check if the input value is '#' and the backspace/delete key is pressed
      if (event.key === "Backspace" || event.key === "Delete") {
        if (this.color.length === 1 && this.color === "#") {
          event.preventDefault(); // Prevent deletion
        }
      }
    },

    setTextColor() {
      let c = this.color?.hex ?? this.color ?? "#";
      if (typeof c !== "string") c = "#";
      if (!c.startsWith("#")) c = "#" + c;
      this.color = expandHexTo6(c);
      this.toggleStyle(this.color, "fill");
      // this.selectedElement.dirty = true;
      window.activeCanvas.renderAll();
      this.fireDebouncedModify();
    },

    fireDebouncedModify() {
      // Empty function which will be overwritten
      // Function body will be set inside mounted hook
    },

    startEditing() {
      if (this.selectedElement.isEditing) {
        this.selectedElement.exitEditing();
      }
      this.selectedElement.enterEditing();
      this.selectedElement.selectAll();
    },

    setFont(font) {
      if (systemFonts.includes(font)) {
        this.selectedElement.set({ fontFamily: font });
        window.activeCanvas.renderAll();
      } else {
        let myfont = new FontFaceObserver(font);
        myfont
            .load()
            .then(() => {
              this.selectedElement.set({ fontFamily: font });
              window.activeCanvas.renderAll();
            })
            .catch(function (e) {
              console.error(e);
            });
      }
    },
  },
};
</script>

<style lang="scss">
#cardDesigner {

  .textOptions {
    padding-left: 1.2rem;
  }

  .textOptions img {
    width: 40px;
    height: 40px;
    padding: 10px;
    box-sizing: border-box;
  }

  .textOptions .colorPickerIcon {
    padding: 9px;
  }

  .activeTab {
    background: var(--color-bg-input);
    border-radius: 15px;
  }

  .rangeInput {
    -webkit-appearance: none !important;
    padding: 0 !important;
    border-radius: 5px !important;
    height: 4px !important;
    background: linear-gradient(
      to right,
      var(--color-border-light) 0%,
      var(--color-border-light) var(--scale-progress, 0%),
      rgba(245, 248, 255, 0.4) var(--scale-progress, 0%),
      rgba(245, 248, 255, 0.4) 100%
    );
    outline: none;


    &::-webkit-slider-thumb {
      appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: var(--color-border-light);
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &::-moz-range-thumb {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: var(--color-border-light);
      cursor: pointer;
      border: none;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &::-moz-range-track {
      height: 4px;
      border-radius: 5px;
      background: linear-gradient(
        to right,
        var(--color-border-light) 0%,
        var(--color-border-light) var(--scale-progress, 0%),
        rgba(245, 248, 255, 0.4) var(--scale-progress, 0%),
        rgba(245, 248, 255, 0.4) 100%
      );
    }

  }

  .vc-chrome {
    width: 100%;
    box-shadow: none;
    background: transparent !important;

    .vc-chrome-saturation-wrap {
      border-radius: 20px;
      padding-bottom: 40% !important;
    }

    .vc-chrome-color-wrap {
      display: none;
    }

    .vc-chrome-body {
      padding-inline: 0;
      background: transparent !important;

      .vc-chrome-hue-wrap .vc-hue {
        border-radius: 20px;
      }
    }
  }

  .transition-icon {
    transition: 0.5s;
  }

  .rotate-icon {
    transform: rotate(180deg);
  }

  .flex-break {
    flex-basis: 100%;
    height: 0;
  }
}
</style>
